import {useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '../../store/store';
import styles from './chat.module.scss';
import {fetchUnreadChat, markMessagesRead, unreadMessagesCountSelector} from '../../store/slices/chat_slice';
import {MessageForm} from './message_form';
import {format} from 'date-fns';
import {Main} from '../../components/main/main.tsx';
import mainStyles from '../../components/main/main.module.scss';
import {Avatar} from '../../components/avatar/avatar.tsx';
import {Size} from '../../enums/size.ts';
import {CustomerFieldKeys, getValue} from '../../support/get_data.ts';

export function Chat() {
    const dispatch = useAppDispatch();
    const chat = useAppSelector((state) => state.chat.chat);
    const appConfig = useAppSelector((state) => state.appConfig.appconfig);
    const messages = chat?.messages;

    const meData = useAppSelector((state) => state.customerInfo.me?.customer);
    const nameData = getValue(meData, CustomerFieldKeys.NAME);
    const image = appConfig?.sliderphoto1;
    const wasAtBottom = useRef(true);
    useEffect(() => {
        if (wasAtBottom.current === true) {
            window.scrollTo(0, document.body.scrollHeight);
        }
    }, [messages]);
    useEffect(() => {
        const listener = () => {
            const isAtBottom = window.scrollY >= document.body.scrollHeight - window.innerHeight;
            if (wasAtBottom.current !== isAtBottom) {
                wasAtBottom.current = isAtBottom;
            }
        };
        window.addEventListener('scroll', listener);
        return () => window.removeEventListener('scroll', listener);
    }, []);

    const token = useAppSelector((state) => state.exchangeToken.token);
    useEffect(() => {
        const interval = setInterval(() => {
            if (token) {
                const lastMessageId = messages?.[messages.length - 1]?.id ?? undefined;
                dispatch(fetchUnreadChat({token, lastMessageId}));
            }
        }, 10000);
        return () => clearInterval(interval);
    }, [token, messages, dispatch]);

    const unreadCount = useAppSelector(unreadMessagesCountSelector);
    useEffect(() => {
        if (unreadCount > 0 && wasAtBottom.current === true && token) {
            dispatch(markMessagesRead({token}));
        }
    }, [unreadCount, token, dispatch]);

    return (
        <Main className={mainStyles.bg_white}>
            <div className={styles.chat}>
                {image != null && (
                    <div className={styles.bg_image}>
                        <img src={image} alt={'background'} onError={(e) => (e.currentTarget.style.display = 'none')} />
                    </div>
                )}
                <div className={styles.content}>
                    {chat?.messages?.map((msg, index) => {
                        return (
                            <div
                                className={`${styles.message} ${msg?.user === null ? styles.me : ''}`}
                                key={`${msg?.id}-${index}`}
                            >
                                {msg?.user !== null && appConfig?.appname && (
                                    <div className={styles.message_avatar}>
                                        <span className={styles.avatar_name}>{appConfig?.appname ?? ''}</span>
                                        <Avatar name={appConfig?.appname} size={Size.Small} />
                                    </div>
                                )}
                                {msg?.user === null && (
                                    <div className={styles.message_avatar}>
                                        <Avatar name={nameData?.value ?? ''} size={Size.Small} />
                                        <span className={styles.avatar_name}>{nameData?.value ?? ''}</span>
                                    </div>
                                )}
                                {msg?.persistedNotification && (
                                    <div className={styles.message_persisted_notification}>
                                        {msg.persistedNotification.message}
                                    </div>
                                )}
                                {msg?.file?.mime_type?.includes('image') && msg?.file?.signed_url != null && (
                                    <img
                                        src={msg?.file?.signed_url}
                                        className={styles.message_image}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                )}
                                {msg?.file?.mime_type?.includes('application') && msg?.file?.signed_url != null && (
                                    <div className={styles.message_file}>
                                        <div className={styles.file_icon}>
                                            <i className="fa-solid fa-file" />
                                        </div>
                                        <div className={styles.file_name}>
                                            <a download={msg?.file?.original_filename} href={msg?.file?.signed_url}>
                                                {msg?.file?.original_filename}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                {msg?.message}
                                <span className={styles.message_date}>
                                    {msg?.created_at_iso8601 &&
                                        format(new Date(msg.created_at_iso8601), 'dd-MM-yyy HH:mm')}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <MessageForm messageCount={messages?.length ?? 0} name={nameData?.value ?? ''} />
            </div>
        </Main>
    );
}
